import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { InputType } from 'config'
import { PaymentType } from 'pages/Admin'
import { LoanProcessRushPaymentRequestTypeTitles } from 'pages/LoanSubmission/constants'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { getAllPaymentType, IStripeIntent } from 'services'
import { Modal, RadioGroup } from 'stories/components'
import { getPrice2decimal, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

const defaultInputs = (): Record<string, InputType> => ({
  feeTitle: {
    inputType: 'text',
    title: 'Title of Fee',
    required: true,
    visible: false,
  },
  reason: {
    inputType: 'textarea',
    title: 'Reason for Change',
    required: true,
    rows: 3,
  },
})

/** What the payment currently says it is for, however it was recorded. */
export const invoiceFeeTitle = (item: Pick<IStripeIntent, 'data'>) =>
  LoanProcessRushPaymentRequestTypeTitles[item.data?.requestType] || item.data?.feeTitle || ''

/**
 * Change what a paid invoice was for. People pay the wrong thing, and this lets someone holding
 * `Loan.Invoice.Reallocate` fix the record without refunding and charging again. The amount is
 * not editable here on purpose: the money moved once, and only its purpose is being corrected.
 */
export const ReallocateInvoiceModal = ({
  item,
  onOk,
  onClose,
}: {
  item: Omit<IStripeIntent, 'id'>
  onOk: (intentId: string, data: { requestType: string; feeTitle?: string; reason: string }) => Promise<any>
  onClose: () => void
}) => {
  const [loading, setLoading] = useState(false)
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([])
  const [requestType, setRequestType] = useState('')
  const [inputs, setInputs] = useState(defaultInputs())

  useEffect(() => {
    setLoading(true)
    getAllPaymentType(item.orderType)
      .then((data) => setPaymentTypes(Array.isArray(data) ? data : []))
      .catch(() => setPaymentTypes([]))
      .finally(() => setLoading(false))
  }, [item.orderType])

  const currentTitle = invoiceFeeTitle(item)

  const options = useMemo(
    () =>
      Object.fromEntries(
        paymentTypes
          .map((t) => [t.id.toString(), `${t.title} - $${getPrice2decimal(t.cost, false, true)}`])
          .concat([['custom', 'Custom']]),
      ),
    [paymentTypes],
  )

  const onChangeRequestType = (value: string) => {
    setRequestType(value)
    const next = cloneDeep(inputs)
    next.feeTitle.visible = value === 'custom'
    next.feeTitle.error = ''
    setInputs(next)
  }

  const onChange = (key: string, value: any) => {
    const next = cloneDeep(inputs)
    next[key].value = value
    next[key].error = InputValidate(next[key])
    setInputs(next)
  }

  const onModalOk = async () => {
    if (!requestType) {
      toast('Pick what the payment is for.', { type: 'error' })
      return
    }
    let hasError = false
    const next = cloneDeep(inputs)
    Object.keys(next).forEach((key) => {
      if (next[key].visible === false) return
      next[key].error = InputValidate(next[key])
      if (next[key].error?.length) hasError = true
    })
    setInputs(next)
    if (hasError) return

    setLoading(true)
    try {
      await onOk(item.intentId, {
        requestType,
        feeTitle: requestType === 'custom' ? String(inputs.feeTitle.value || '').trim() : undefined,
        reason: String(inputs.reason.value || '').trim(),
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal title="Reallocate Payment" isOpen onOk={onModalOk} onClose={onClose} titleOkay="Reallocate" disabled={!requestType}>
      <div className="w-[calc(100vw-5rem)] max-w-[36rem] relative">
        <LayoutLoading show={loading} />
        <div className="rounded border border-gray-200 bg-gray-50 px-4 py-3 text-sm mb-4">
          <p>
            <span className="text-gray-500">Payment:</span> <span className="font-semibold">{item.intentId}</span> ·{' '}
            <span className="font-semibold">${getPrice2decimal(item.amount, false, true)}</span>
          </p>
          <p>
            <span className="text-gray-500">Currently for:</span> <span className="font-semibold">{currentTitle || '-'}</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Only what this payment is for changes. The charge, amount and payer stay as they are.
          </p>
        </div>

        <RadioGroup id="reallocate-request-type" title="Change to" options={options} value={requestType} onChange={onChangeRequestType} />

        {Object.keys(inputs)
          .filter((key) => inputs[key].visible !== false)
          .map((key) => (
            <div key={key} className="mt-4">
              <RenderInput input={inputs[key]} Key={key} onChange={onChange} />
            </div>
          ))}
      </div>
    </Modal>
  )
}
